'use client'

import dynamic from 'next/dynamic'
import { Search } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'

import { cn } from '@/utilities/ui'

const SearchDialog = dynamic(
  () => import('./SearchDialog').then((m) => m.SearchDialog),
  { ssr: false },
)

type Props = {
  className?: string
  placeholder?: string
}

/**
 * Gatilho da busca global no header. Visual de campo de busca, mas abre o modal
 * (command palette). Atalhos: Ctrl/Cmd+K e "/". O modal é carregado sob demanda
 * (lazy) no primeiro uso e mantido montado para a animação de saída.
 */
export function GlobalSearchTrigger({ className, placeholder: placeholderProp }: Props) {
  const t = useTranslations('Search')
  const placeholder = placeholderProp ?? t('dialogPlaceholder')
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    if (open) setMounted(true)
  }, [open])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setOpen((o) => !o)
        return
      }
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const t = e.target as HTMLElement | null
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
        e.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'relative flex h-8 w-full min-w-0 items-center gap-2 rounded-md border border-input bg-muted/60 pl-9 pr-2 text-left text-sm text-muted-foreground shadow-xs transition-colors hover:bg-muted',
          className,
        )}
      >
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-[15px] -translate-y-1/2"
          aria-hidden
        />
        <span className="truncate">{placeholder}</span>
        <kbd className="pointer-events-none ml-auto hidden shrink-0 rounded border border-border bg-muted/80 px-1.5 py-0.5 font-mono text-[10px] lg:inline">
          Ctrl K
        </kbd>
      </button>

      {mounted ? <SearchDialog open={open} onOpenChange={setOpen} /> : null}
    </>
  )
}
